// DailyStreetSheet.js v3.0.0 — Highline-template street sheet
// Evening-before workflow: report today's production + tomorrow's plan + personnel
// Auto-emails to Aspen, no access code gate, Spanish support
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Plus, Trash2, Loader, Users, ClipboardList,
  Send, CheckCircle, ChevronDown, ChevronUp, Globe
} from 'lucide-react';
import { colors, STREET_SHEET_CONFIG } from '../config/constants';
import { useTranslation } from '../config/translations';
import { useMobile } from '../utils/theme';

const ACTIVITY_TYPES = STREET_SHEET_CONFIG.activityTypes || ['Bore', 'Plow', 'Pull', 'Splice', 'Set Fixtures', 'Restoration', 'Other'];
const ROLES = STREET_SHEET_CONFIG.roles || ['Crew Lead', 'Bore Operator', 'Pull Operator', 'Laborer', 'Flagger', 'Spotter', 'Other'];

const DailyStreetSheet = ({ darkMode, user, setCurrentPage, loggedInUser }) => {
  const { t, lang, toggleLang } = useTranslation();
  const isMobile = useMobile();

  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#22c55e' : '#16a34a';
  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const warningBg = darkMode ? '#3a2a1a' : '#fef3c7';
  const warningBorder = darkMode ? '#854d0e' : '#fbbf24';
  const warningText = darkMode ? '#fbbf24' : '#92400e';

  // Parse URL params
  const getUrlParams = () => {
    try {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return {
        project: params.get('project') || 'BASTROP',
        from: params.get('from') || null,
      };
    } catch { return { project: 'BASTROP', from: null }; }
  };
  const { project, from } = getUrlParams();

  // Determine report date (tomorrow, or weekend range on Friday)
  const today = new Date();
  const isFriday = today.getDay() === 5;
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const formatDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });

  const getReportDateLabel = () => {
    if (isFriday) {
      const sat = new Date(today); sat.setDate(sat.getDate() + 1);
      const mon = new Date(today); mon.setDate(mon.getDate() + 3);
      return `${formatDate(sat)} - ${formatDate(mon)}`;
    }
    return formatDate(tomorrow);
  };

  const getReportDateValue = () => {
    return tomorrow.toISOString().split('T')[0];
  };

  // Deadline countdown
  const [deadlineInfo, setDeadlineInfo] = useState('');
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const deadline = new Date(now);
      deadline.setHours(STREET_SHEET_CONFIG.deadlineHour || 19, 0, 0, 0);
      const diff = deadline - now;
      if (diff > 0 && diff < 2 * 60 * 60 * 1000) {
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(mins / 60);
        const m = mins % 60;
        setDeadlineInfo(hrs > 0 ? `${hrs}h ${m}m remaining` : `${m}m remaining`);
      } else if (diff <= 0) {
        setDeadlineInfo('Past deadline!');
      } else {
        setDeadlineInfo('');
      }
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, []);

  // Form state
  const [contractor, setContractor] = useState('LYT Communications');
  const [county, setCounty] = useState(STREET_SHEET_CONFIG.county || 'Bastrop');
  const [workingTomorrow, setWorkingTomorrow] = useState(true);
  const [notWorkingReason, setNotWorkingReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Section collapse state
  const [showTomorrowPlan, setShowTomorrowPlan] = useState(true);
  const [showProduction, setShowProduction] = useState(true);
  const [showPersonnel, setShowPersonnel] = useState(true);

  // Tomorrow's Plan rows
  const [tomorrowPlan, setTomorrowPlan] = useState([
    { id: 1, crewLeader: '', streetName: '', cabinet: '', activity: '' },
  ]);

  // Today's Production rows
  const [todayProduction, setTodayProduction] = useState([
    { id: 1, crewLeader: '', activityType: '', qty: '', cabinet: '', streetName: '', completed: true },
  ]);

  // Personnel list
  const [personnel, setPersonnel] = useState([
    { id: 1, name: '', role: '' },
  ]);

  // Row management helpers
  const addPlanRow = () => setTomorrowPlan(prev => [...prev, { id: Date.now(), crewLeader: '', streetName: '', cabinet: '', activity: '' }]);
  const removePlanRow = (id) => { if (tomorrowPlan.length > 1) setTomorrowPlan(prev => prev.filter(r => r.id !== id)); };
  const updatePlanRow = (id, field, value) => setTomorrowPlan(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const addProductionRow = () => setTodayProduction(prev => [...prev, { id: Date.now(), crewLeader: '', activityType: '', qty: '', cabinet: '', streetName: '', completed: true }]);
  const removeProductionRow = (id) => { if (todayProduction.length > 1) setTodayProduction(prev => prev.filter(r => r.id !== id)); };
  const updateProductionRow = (id, field, value) => setTodayProduction(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const addPerson = () => setPersonnel(prev => [...prev, { id: Date.now(), name: '', role: '' }]);
  const removePerson = (id) => { if (personnel.length > 1) setPersonnel(prev => prev.filter(r => r.id !== id)); };
  const updatePerson = (id, field, value) => setPersonnel(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  // Validation
  const isValid = () => {
    if (!contractor.trim()) return false;
    // Must have at least one production row with data
    const hasProduction = todayProduction.some(r => r.crewLeader.trim() && r.activityType && r.streetName.trim());
    if (!hasProduction) return false;
    // Must have at least one person
    const hasPerson = personnel.some(r => r.name.trim());
    if (!hasPerson) return false;
    // If working tomorrow, must have at least one plan row
    if (workingTomorrow) {
      const hasPlan = tomorrowPlan.some(r => r.crewLeader.trim() && r.streetName.trim() && r.activity);
      if (!hasPlan) return false;
    }
    // If not working, must have reason
    if (!workingTomorrow && !notWorkingReason.trim()) return false;
    return true;
  };

  // Submit
  const handleSubmit = async () => {
    if (!isValid()) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const reportDate = getReportDateValue();
      const submittedBy = personnel.find(p => p.role === 'Crew Lead')?.name || personnel[0]?.name || 'Unknown';

      // Filter out empty rows
      const planData = workingTomorrow
        ? tomorrowPlan.filter(r => r.crewLeader.trim() || r.streetName.trim()).map(r => ({
            crewLeader: r.crewLeader.trim(),
            streetName: r.streetName.trim(),
            cabinet: r.cabinet.trim(),
            activity: r.activity,
          }))
        : [];

      const productionData = todayProduction.filter(r => r.crewLeader.trim() || r.streetName.trim()).map(r => ({
        crewLeader: r.crewLeader.trim(),
        activityType: r.activityType,
        qty: r.qty,
        cabinet: r.cabinet.trim(),
        streetName: r.streetName.trim(),
        completed: r.completed,
      }));

      const crewData = personnel.filter(r => r.name.trim()).map(r => ({
        name: r.name.trim(),
        role: r.role,
      }));

      const body = {
        project_id: project,
        sheet_date: today.toISOString().split('T')[0],
        report_date: reportDate,
        company_name: contractor.trim(),
        contact_name: submittedBy,
        county: county.trim(),
        working_tomorrow: workingTomorrow,
        not_working_reason: workingTomorrow ? null : notWorkingReason.trim(),
        tomorrow_plan: JSON.stringify(planData),
        today_production: JSON.stringify(productionData),
        crew_members: JSON.stringify(crewData),
        submitted_by: submittedBy,
        submitted_by_email: user?.email || loggedInUser?.email || '',
      };

      // Save to DB
      const resp = await fetch('/api/street-sheets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await resp.json();
      if (!result.success) throw new Error(result.error || 'Failed to save');

      // Trigger email
      if (result.data?.id) {
        try {
          await fetch('/api/street-sheet-submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sheet_id: result.data.id }),
          });
        } catch (emailErr) {
          console.error('Email send failed:', emailErr);
        }
      }

      setSubmitted(true);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Connection error — check your internet and try again' });
      setTimeout(() => setMessage(null), 4000);
    }
    setLoading(false);
  };

  const goBack = () => {
    if (from === 'hub') {
      window.location.hash = `#field-hub?project=${project}`;
    } else {
      const role = (user || loggedInUser)?.role || 'contractor';
      if (role === 'admin') setCurrentPage('admin-dashboard');
      else if (role === 'contractor') setCurrentPage('contractor-dashboard');
      else setCurrentPage('employee-dashboard');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 12px',
    border: `1px solid ${borderColor}`,
    borderRadius: '8px',
    backgroundColor: darkMode ? '#1a2332' : '#ffffff',
    color: textColor,
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = { ...inputStyle, appearance: 'auto' };
  const labelStyle = { display: 'block', marginBottom: '5px', color: mutedColor, fontSize: '0.8rem', fontWeight: '500' };

  const sectionHeaderStyle = (expanded) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    cursor: 'pointer',
    backgroundColor: darkMode ? '#131f30' : '#f0f4f8',
    borderBottom: expanded ? `1px solid ${borderColor}` : 'none',
    borderRadius: expanded ? '12px 12px 0 0' : '12px',
  });

  // Success screen
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{
          backgroundColor: cardBg, borderRadius: '16px', padding: '40px',
          border: `1px solid ${borderColor}`, maxWidth: '500px', width: '100%', textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        }}>
          <CheckCircle size={60} style={{ color: accentSecondary, marginBottom: '16px' }} />
          <h2 style={{ color: textColor, fontSize: '1.3rem', fontWeight: '700', margin: '0 0 8px' }}>
            {t('ss_success')}
          </h2>
          <p style={{ color: mutedColor, fontSize: '0.9rem', margin: '0 0 24px' }}>
            {getReportDateLabel()}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {from === 'hub' && (
              <button
                onClick={() => { window.location.hash = `#field-hub?project=${project}`; }}
                style={{
                  padding: '12px 24px', backgroundColor: accentPrimary, color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600',
                }}
              >
                {t('ss_back_to_hub')}
              </button>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px', backgroundColor: 'transparent', color: textColor,
                border: `1px solid ${borderColor}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem',
              }}
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: isMobile ? '16px 12px' : '24px 16px' }}>
      {/* Toast */}
      {message && (
        <div style={{
          position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
          padding: '14px 28px', backgroundColor: message.type === 'success' ? accentSecondary : '#ef4444',
          color: '#fff', borderRadius: '10px', zIndex: 1000, fontSize: '0.9rem', fontWeight: '500',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)', maxWidth: '90vw', textAlign: 'center',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button onClick={goBack} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
            backgroundColor: 'transparent', border: `1px solid ${borderColor}`,
            borderRadius: '8px', color: textColor, cursor: 'pointer', fontSize: '0.85rem',
          }}>
            <ArrowLeft size={16} /> {from === 'hub' ? t('ss_back_to_hub') : t('ss_back')}
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: isMobile ? '1.2rem' : '1.4rem', fontWeight: '700', color: textColor,
              margin: 0, display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <ClipboardList size={22} style={{ color: accentSecondary }} />
              {t('ss_title')}
            </h1>
            <p style={{ color: mutedColor, margin: '2px 0 0', fontSize: '0.8rem' }}>
              {t('ss_subtitle')}
            </p>
          </div>
          <button onClick={toggleLang} style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
            backgroundColor: darkMode ? '#1e293b' : '#ffffff', border: `1px solid ${borderColor}`,
            borderRadius: '20px', color: textColor, cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
          }}>
            <Globe size={14} /> {lang === 'en' ? 'ES' : 'EN'}
          </button>
        </div>

        {/* Deadline Banner */}
        <div style={{
          padding: '12px 16px', marginBottom: '16px',
          backgroundColor: warningBg, border: `1px solid ${warningBorder}`,
          borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '8px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.85rem', color: warningText, fontWeight: '600' }}>
            {isFriday ? t('ss_weekend') : t('ss_deadline')}
          </span>
          {deadlineInfo && (
            <span style={{ fontSize: '0.8rem', color: warningText, fontWeight: '700' }}>
              {deadlineInfo}
            </span>
          )}
        </div>

        {/* Header Info Card */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', padding: '20px', marginBottom: '16px',
          border: `1px solid ${borderColor}`,
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: '12px',
          }}>
            <div>
              <label style={labelStyle}>{t('ss_project_code')}</label>
              <div style={{
                padding: '11px 12px', backgroundColor: darkMode ? '#1a2332' : '#f0f4f8',
                borderRadius: '8px', fontSize: '0.9rem', color: textColor, fontWeight: '600',
                border: `1px solid ${borderColor}`,
              }}>
                {STREET_SHEET_CONFIG.projectCode || 'HLBSMI1977'}
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t('ss_for_date')}</label>
              <div style={{
                padding: '11px 12px', backgroundColor: darkMode ? '#1a2332' : '#f0f4f8',
                borderRadius: '8px', fontSize: '0.9rem', color: textColor, fontWeight: '500',
                border: `1px solid ${borderColor}`,
              }}>
                {getReportDateLabel()}
              </div>
            </div>
            <div>
              <label style={labelStyle}>{t('ss_contractor')}</label>
              <input type="text" value={contractor} onChange={(e) => setContractor(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>{t('ss_county')}</label>
              <input type="text" value={county} onChange={(e) => setCounty(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Section 1: Working Tomorrow? */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', marginBottom: '16px',
          border: `1px solid ${borderColor}`, overflow: 'hidden',
        }}>
          <div style={{
            padding: '16px 20px',
            backgroundColor: darkMode ? '#131f30' : '#f0f4f8',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: textColor, margin: 0 }}>
              {t('ss_working_tomorrow')}
            </h3>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: workingTomorrow ? 0 : '12px' }}>
              <button onClick={() => setWorkingTomorrow(true)} style={{
                padding: '10px 28px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem',
                border: `2px solid ${workingTomorrow ? accentSecondary : borderColor}`,
                backgroundColor: workingTomorrow ? accentSecondary + '20' : 'transparent',
                color: workingTomorrow ? accentSecondary : textColor,
              }}>
                {t('ss_yes')}
              </button>
              <button onClick={() => setWorkingTomorrow(false)} style={{
                padding: '10px 28px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem',
                border: `2px solid ${!workingTomorrow ? '#ef4444' : borderColor}`,
                backgroundColor: !workingTomorrow ? '#ef444420' : 'transparent',
                color: !workingTomorrow ? '#ef4444' : textColor,
              }}>
                {t('ss_no')}
              </button>
            </div>
            {!workingTomorrow && (
              <div>
                <label style={labelStyle}>{t('ss_reason')} *</label>
                <input type="text" value={notWorkingReason} onChange={(e) => setNotWorkingReason(e.target.value)}
                  placeholder={lang === 'es' ? 'Ej: lluvia, equipo, dia libre' : 'e.g. rain, equipment, day off'}
                  style={inputStyle}
                />
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Tomorrow's Plan (only if working) */}
        {workingTomorrow && (
          <div style={{
            backgroundColor: cardBg, borderRadius: '12px', marginBottom: '16px',
            border: `1px solid ${borderColor}`, overflow: 'hidden',
          }}>
            <div onClick={() => setShowTomorrowPlan(!showTomorrowPlan)} style={sectionHeaderStyle(showTomorrowPlan)}>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', color: textColor, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('ss_tomorrows_plan')}
              </h3>
              {showTomorrowPlan ? <ChevronUp size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
            </div>
            {showTomorrowPlan && (
              <div style={{ padding: '16px 20px' }}>
                {tomorrowPlan.map((row, idx) => (
                  <div key={row.id} style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr auto',
                    gap: '10px', marginBottom: '10px', alignItems: 'end',
                  }}>
                    <div>
                      {idx === 0 && <label style={labelStyle}>{t('ss_crew_leader')} *</label>}
                      <input type="text" value={row.crewLeader} onChange={(e) => updatePlanRow(row.id, 'crewLeader', e.target.value)} placeholder="Name" style={inputStyle} />
                    </div>
                    <div>
                      {idx === 0 && <label style={labelStyle}>{t('ss_street_name')} *</label>}
                      <input type="text" value={row.streetName} onChange={(e) => updatePlanRow(row.id, 'streetName', e.target.value)} placeholder="Street" style={inputStyle} />
                    </div>
                    <div>
                      {idx === 0 && <label style={labelStyle}>{t('ss_cabinet')}</label>}
                      <input type="text" value={row.cabinet} onChange={(e) => updatePlanRow(row.id, 'cabinet', e.target.value)} placeholder="CAB-###" style={inputStyle} />
                    </div>
                    <div>
                      {idx === 0 && <label style={labelStyle}>{t('ss_activity')} *</label>}
                      <select value={row.activity} onChange={(e) => updatePlanRow(row.id, 'activity', e.target.value)} style={selectStyle}>
                        <option value="">{t('ss_select_activity')}</option>
                        {ACTIVITY_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </div>
                    {tomorrowPlan.length > 1 && (
                      <button onClick={() => removePlanRow(row.id)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '10px 4px',
                      }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={addPlanRow} style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                  backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', marginTop: '4px',
                }}>
                  <Plus size={14} /> {t('ss_add_row')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Section 3: Today's Production */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', marginBottom: '16px',
          border: `1px solid ${borderColor}`, overflow: 'hidden',
        }}>
          <div onClick={() => setShowProduction(!showProduction)} style={sectionHeaderStyle(showProduction)}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: textColor, margin: 0 }}>
              {t('ss_todays_production')}
            </h3>
            {showProduction ? <ChevronUp size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
          </div>
          {showProduction && (
            <div style={{ padding: '16px 20px' }}>
              {todayProduction.map((row, idx) => (
                <div key={row.id} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 0.7fr 0.8fr 1fr 0.6fr auto',
                  gap: '10px', marginBottom: '10px', alignItems: 'end',
                }}>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_crew_leader')} *</label>}
                    <input type="text" value={row.crewLeader} onChange={(e) => updateProductionRow(row.id, 'crewLeader', e.target.value)} placeholder="Name" style={inputStyle} />
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_activity_type')} *</label>}
                    <select value={row.activityType} onChange={(e) => updateProductionRow(row.id, 'activityType', e.target.value)} style={selectStyle}>
                      <option value="">{t('ss_select_activity')}</option>
                      {ACTIVITY_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_qty')} *</label>}
                    <input type="number" value={row.qty} onChange={(e) => updateProductionRow(row.id, 'qty', e.target.value)} placeholder="0" style={inputStyle} />
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_cabinet')}</label>}
                    <input type="text" value={row.cabinet} onChange={(e) => updateProductionRow(row.id, 'cabinet', e.target.value)} placeholder="CAB-###" style={inputStyle} />
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_street_name')} *</label>}
                    <input type="text" value={row.streetName} onChange={(e) => updateProductionRow(row.id, 'streetName', e.target.value)} placeholder="Street" style={inputStyle} />
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_completed')}</label>}
                    <button onClick={() => updateProductionRow(row.id, 'completed', !row.completed)} style={{
                      width: '100%', padding: '11px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600',
                      border: `1px solid ${row.completed ? accentSecondary : borderColor}`,
                      backgroundColor: row.completed ? accentSecondary + '20' : 'transparent',
                      color: row.completed ? accentSecondary : mutedColor,
                    }}>
                      {row.completed ? t('ss_yes') : t('ss_no')}
                    </button>
                  </div>
                  {todayProduction.length > 1 && (
                    <button onClick={() => removeProductionRow(row.id)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '10px 4px',
                    }}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addProductionRow} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', marginTop: '4px',
              }}>
                <Plus size={14} /> {t('ss_add_row')}
              </button>
            </div>
          )}
        </div>

        {/* Section 4: Personnel */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', marginBottom: '24px',
          border: `1px solid ${borderColor}`, overflow: 'hidden',
        }}>
          <div onClick={() => setShowPersonnel(!showPersonnel)} style={sectionHeaderStyle(showPersonnel)}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: textColor, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} style={{ color: accentPrimary }} />
              {t('ss_personnel')}
            </h3>
            {showPersonnel ? <ChevronUp size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
          </div>
          {showPersonnel && (
            <div style={{ padding: '16px 20px' }}>
              {personnel.map((row, idx) => (
                <div key={row.id} style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr auto',
                  gap: '10px', marginBottom: '10px', alignItems: 'end',
                }}>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_name')} *</label>}
                    <input type="text" value={row.name} onChange={(e) => updatePerson(row.id, 'name', e.target.value)} placeholder="Full name" style={inputStyle} />
                  </div>
                  <div>
                    {idx === 0 && <label style={labelStyle}>{t('ss_role')}</label>}
                    <select value={row.role} onChange={(e) => updatePerson(row.id, 'role', e.target.value)} style={selectStyle}>
                      <option value="">{t('ss_select_role')}</option>
                      {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {personnel.length > 1 && (
                    <button onClick={() => removePerson(row.id)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '10px 4px',
                    }}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={addPerson} style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', marginTop: '4px',
              }}>
                <Plus size={14} /> {t('ss_add_person')}
              </button>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', padding: '20px',
          border: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px',
        }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: mutedColor }}>
              {contractor} &mdash; {getReportDateLabel()}
            </div>
            <div style={{ fontSize: '0.8rem', color: mutedColor, marginTop: '2px' }}>
              {todayProduction.filter(r => r.crewLeader.trim()).length} production rows &bull; {personnel.filter(r => r.name.trim()).length} personnel
            </div>
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !isValid()}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '14px 32px', backgroundColor: isValid() ? accentSecondary : borderColor, color: '#fff',
              border: 'none', borderRadius: '10px',
              cursor: loading || !isValid() ? 'not-allowed' : 'pointer',
              fontSize: '1rem', fontWeight: '600', opacity: loading ? 0.7 : 1,
              boxShadow: isValid() ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            }}
          >
            {loading ? <Loader size={18} className="spin" /> : <Send size={18} />}
            {loading ? t('ss_submitting') : t('ss_submit')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
        @media (max-width: 600px) {
          div[style*="gridTemplateColumns: repeat(auto-fit"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DailyStreetSheet;
